import { useCallback } from "react";
import type { VehicleFilters, TrayType } from "@visioni/a3s-vehicle-tracker";

type FiltersChangeHandler = <K extends keyof VehicleFilters>(key: K, value: VehicleFilters[K]) => void;

export interface UseAttributeFiltersReturn {
  isTowingTrailer: boolean;
  trayValue: TrayType | null;
  signTextValue: string;
  handleTrailerToggle: () => void;
  handleTrayChange: (value: TrayType | null) => void;
  handleSignTextChange: (value: string) => void;
}

/**
 * Reads and writes the three forensic attributes VT still filters on, from the opaque
 * `filters.attributes` record.
 *
 * Shared by FilterPanel and FilterPanel.native, which carried byte-identical copies of this.
 *
 * Each value is narrowed by `typeof` rather than trusted: the record round-trips through the URL
 * (see `filter-params.ts`), so a stale bookmark can carry a value of the wrong shape for a key —
 * a `tray: true` from before the tray-type filter existed, say. Those are discarded rather than
 * rendered. Writes delete the key outright when cleared, since VT treats an absent key and a null
 * one differently.
 */
export function useAttributeFilters(
  attributes: VehicleFilters["attributes"],
  onFiltersChange: FiltersChangeHandler,
): UseAttributeFiltersReturn {
  const isTowingTrailer = attributes.trailer === true;

  const trayValue: TrayType | null = typeof attributes.tray === "string" ? (attributes.tray as TrayType) : null;

  const signTextValue: string = typeof attributes.sign_text === "string" ? attributes.sign_text : "";

  const handleTrailerToggle = useCallback(() => {
    const { trailer: _trailer, ...rest } = attributes;
    onFiltersChange("attributes", isTowingTrailer ? rest : { ...rest, trailer: true });
  }, [attributes, isTowingTrailer, onFiltersChange]);

  const handleTrayChange = useCallback(
    (value: TrayType | null) => {
      const { tray: _tray, ...rest } = attributes;
      onFiltersChange("attributes", value ? { ...rest, tray: value } : rest);
    },
    [attributes, onFiltersChange],
  );

  const handleSignTextChange = useCallback(
    (value: string) => {
      const { sign_text: _signText, ...rest } = attributes;
      onFiltersChange("attributes", value ? { ...rest, sign_text: value } : rest);
    },
    [attributes, onFiltersChange],
  );

  return {
    isTowingTrailer,
    trayValue,
    signTextValue,
    handleTrailerToggle,
    handleTrayChange,
    handleSignTextChange,
  };
}
